import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Loader, colors } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { useLockedInvestmentCaseStatus } from "Components/Analyses/_Shared/useLockedInvestmentCaseStatus";
import {
  getGetModelingRecapitulationQueryKey,
  useGetModelingAvailableAmount,
  useGetModelingAvailableAssetAmount,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { useResponsive } from "Infrastructure/Hooks/useResponsive";
import { Spacing, TABLET_MAX_WIDTH } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext, useEffect } from "react";
import { GoalModelingTable } from "./GoalModelingTable";
import { ModelingAlert } from "./ModelingAlert";
import { ModelingInsurance } from "./ModelingInsurance";
import { RecapitulationTooltip } from "./RecapitulationTooltip";

export const ModelingPane: FC = () => {
  const isTablet = useResponsive(TABLET_MAX_WIDTH);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { lockedCaseStatus, isInvestmentCaseLocked, isInvestmentStatesPending } = useLockedInvestmentCaseStatus(analysisId, versionId);

  const { data: availableAmount, isLoading: isAvailableAmountLoading } = useGetModelingAvailableAmount(analysisId, versionId);
  const { data: availableAssetAmount, isLoading: isAvailableAssetAmountLoading } = useGetModelingAvailableAssetAmount(
    analysisId,
    versionId,
  );

  // Refetch recapitulation query every time we enter the ModelingPane
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
  }, [queryClient, analysisId, versionId]);

  if (isInvestmentStatesPending || isAvailableAmountLoading || isAvailableAssetAmountLoading) {
    return <Loader />;
  }

  return (
    <InvestmentCaseContext.Provider value={{ lockedCaseStatus, isInvestmentCaseLocked }}>
      <Box sx={{ marginBottom: "120px !important", marginTop: "0px !important" }}>
        <Grid container height={theme.spacing(16)} columnSpacing={Spacing.XXXL} sx={{ borderBottom: `1px solid ${colors.borderLight}` }}>
          <Grid size={isTablet ? 3 : 4}>
            <Box height="100%" display="flex" alignItems="center" justifyContent="space-between">
              <Typography fontWeight={600}>{t(Resources.AnalysisDetail.Modeling.AvailableResources)}</Typography>
            </Box>
          </Grid>
          <Grid size={isTablet ? 5 : 4}>
            <Box height="100%" display="flex" alignItems="center" justifyContent="space-between">
              <Typography fontWeight={600}>{t(Resources.AnalysisDetail.Modeling.FinancialAssets)}</Typography>
              <Box display="flex">
                <Box mr={Spacing.M} display="flex" flexDirection="column">
                  <Typography color="secondary" variant="caption">
                    {t(Resources.AnalysisDetail.Modeling.RemainingForRedistribution)}
                  </Typography>
                  <Typography
                    textAlign="right"
                    variant="body2"
                    color={availableAssetAmount && availableAssetAmount?.amount.value < 0 ? "error" : "textPrimary"}
                    fontWeight={availableAssetAmount && availableAssetAmount?.amount.value < 0 ? 500 : 400}
                  >
                    {formatCurrency(availableAssetAmount?.amount.value, availableAssetAmount?.amount.currencyCode, 0)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <RecapitulationTooltip />
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid size={4}>
            <Box height="100%" display="flex" alignItems="center" justifyContent="space-between" minWidth="250px !important">
              <Typography fontWeight={600}>{t(Resources.AnalysisDetail.Modeling.Recurring)}</Typography>
              <Box display="flex">
                <Box mr={Spacing.M} display="flex" flexDirection="column">
                  <Typography color="secondary" variant="caption">
                    {t(Resources.AnalysisDetail.Modeling.RemainingForRedistribution)}
                  </Typography>
                  <Typography
                    color={availableAmount && availableAmount?.amount.value < 0 ? "error" : "textPrimary"}
                    fontWeight={availableAmount && availableAmount?.amount.value < 0 ? 500 : 400}
                    textAlign="right"
                    variant="body2"
                  >
                    {formatCurrency(availableAmount?.amount.value, availableAmount?.amount.currencyCode, 0)}/{t(Resources.Common.Month)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <RecapitulationTooltip />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Stack py={Spacing.XL} spacing={Spacing.XL}>
          <ModelingInsurance />
          <Divider />
          <Stack spacing={Spacing.XL}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t(Resources.AnalysisDetail.Modeling.GoalCoverage)}
            </Typography>
            {(availableAmount && availableAmount.amount.value < 0) || (availableAssetAmount && availableAssetAmount.amount.value < 0) ? (
              <ModelingAlert />
            ) : null}
          </Stack>
          <GoalModelingTable />
        </Stack>
      </Box>
    </InvestmentCaseContext.Provider>
  );
};
